import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import {
  User,
  Home,
  Flame,
  Zap,
  Target,
  CheckCircle,
  ChevronLeft,
  ChevronRight,
  Loader2
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Progress } from "../components/ui/progress";

const API = `${process.env.REACT_APP_BACKEND_URL || window.location.origin}/api`;

const STEPS = [
  { id: 1, title: "Informations personnelles", icon: User },
  { id: 2, title: "Caractéristiques du logement", icon: Home },
  { id: 3, title: "Système de chauffage", icon: Flame },
  { id: 4, title: "Consommation énergétique", icon: Zap },
  { id: 5, title: "Objectifs et priorités", icon: Target },
  { id: 6, title: "Confirmation", icon: CheckCircle },
];

const INITIAL_STATE = {
  personal_info: {
    nom: "",
    prenom: "",
    email: "",
    telephone: "",
    adresse: ""
  },
  property_info: {
    type_bien: "",
    annee_construction: "",
    surface: "",
    nombre_pieces: "",
    nombre_etages: "",
    type_vitrage: ""
  },
  heating_info: {
    type_chauffage: "",
    age_chauffage: "",
    type_eau_chaude: "",
    ventilation: ""
  },
  energy_consumption: {
    facture_annuelle: "",
    fournisseur: "",
    classe_energetique: ""
  },
  objectives: {
    objectif_principal: "",
    travaux_envisages: [],
    autres_travaux: "",
    description_projet: "",
    budget: "",
    delai: "",
    pieces_concernees: []
  },
  selected_pack: "",
  selected_service: "",
  rgpd_consent: false
};

// Save/Load from localStorage
const STORAGE_KEY = "eco_conseil_simulation";

export default function SimulationPage({ embedded = false }) {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState(INITIAL_STATE);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  // Load saved data and selected pack/service on mount
  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      try {
        const parsedData = JSON.parse(saved);
        // Ensure objectives has all required fields
        const safeData = {
          ...INITIAL_STATE,
          ...parsedData,
          objectives: {
            ...INITIAL_STATE.objectives,
            ...(parsedData.objectives || {}),
            travaux_envisages: parsedData.objectives?.travaux_envisages || [],
            pieces_concernees: parsedData.objectives?.pieces_concernees || []
          }
        };
        setFormData(safeData);
      } catch {
        // Clear corrupted data
        localStorage.removeItem(STORAGE_KEY);
      }
    }
    
    // Check for selected pack or service from pricing page
    const selectedPack = localStorage.getItem("selected_pack");
    const selectedService = localStorage.getItem("selected_service");
    
    if (selectedPack) {
      setFormData(prev => ({ ...prev, selected_pack: selectedPack }));
      localStorage.removeItem("selected_pack");
    }
    if (selectedService) {
      setFormData(prev => ({ ...prev, selected_service: selectedService }));
      localStorage.removeItem("selected_service");
    }
  }, []);

  // Auto-save on data change
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(formData));
  }, [formData]);

  const updateField = (section, field, value) => {
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
    // Clear error for this field
    setErrors(prev => ({ ...prev, [`${section}.${field}`]: null }));
  };

  const toggleTravaux = (travail) => {
    setFormData(prev => {
      const current = prev.objectives?.travaux_envisages || [];
      const updated = current.includes(travail)
        ? current.filter(t => t !== travail)
        : [...current, travail];
      return {
        ...prev,
        objectives: { 
          ...prev.objectives, 
          travaux_envisages: updated,
          pieces_concernees: prev.objectives?.pieces_concernees || []
        }
      };
    });
  };

  const validateStep = (step) => {
    const newErrors = {};
    
    switch (step) {
      case 1:
        if (!formData.personal_info.nom) newErrors["personal_info.nom"] = "Requis";
        if (!formData.personal_info.prenom) newErrors["personal_info.prenom"] = "Requis";
        if (!formData.personal_info.email) newErrors["personal_info.email"] = "Requis";
        if (!formData.personal_info.telephone) newErrors["personal_info.telephone"] = "Requis";
        if (!formData.personal_info.adresse) newErrors["personal_info.adresse"] = "Requis";
        break;
      case 2:
        if (!formData.property_info.type_bien) newErrors["property_info.type_bien"] = "Requis";
        if (!formData.property_info.annee_construction) newErrors["property_info.annee_construction"] = "Requis";
        if (!formData.property_info.surface) newErrors["property_info.surface"] = "Requis";
        break;
      case 3:
        if (!formData.heating_info.type_chauffage) newErrors["heating_info.type_chauffage"] = "Requis";
        break;
      case 4:
        if (!formData.energy_consumption.facture_annuelle) newErrors["energy_consumption.facture_annuelle"] = "Requis";
        break;
      case 5:
        if (!formData.objectives?.objectif_principal) newErrors["objectives.objectif_principal"] = "Requis";
        if ((formData.objectives?.travaux_envisages || []).length === 0) newErrors["objectives.travaux_envisages"] = "Sélectionnez au moins un type de travaux";
        break;
      case 6:
        if (!formData.rgpd_consent) newErrors["rgpd_consent"] = "Vous devez accepter les conditions";
        break;
      default:
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      if (currentStep < 6) {
        setCurrentStep(prev => prev + 1);
      }
    }
  };

  const handlePrev = () => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleSubmit = async () => {
    if (!validateStep(6)) return;

    setLoading(true);
    
    // Build payload with safe defaults
    const payload = {
      personal_info: {
        nom: formData.personal_info?.nom || "",
        prenom: formData.personal_info?.prenom || "",
        email: formData.personal_info?.email || "",
        telephone: formData.personal_info?.telephone || "",
        adresse: formData.personal_info?.adresse || ""
      },
      property_info: {
        type_bien: formData.property_info?.type_bien || "",
        annee_construction: formData.property_info?.annee_construction || "",
        surface: parseInt(formData.property_info?.surface) || 0,
        nombre_pieces: parseInt(formData.property_info?.nombre_pieces) || 0,
        nombre_etages: parseInt(formData.property_info?.nombre_etages) || 0,
        type_vitrage: formData.property_info?.type_vitrage || ""
      },
      heating_info: {
        type_chauffage: formData.heating_info?.type_chauffage || "",
        age_chauffage: formData.heating_info?.age_chauffage || "",
        type_eau_chaude: formData.heating_info?.type_eau_chaude || "",
        ventilation: formData.heating_info?.ventilation || ""
      },
      energy_consumption: {
        facture_annuelle: parseInt(formData.energy_consumption?.facture_annuelle) || 0,
        fournisseur: formData.energy_consumption?.fournisseur || "",
        classe_energetique: formData.energy_consumption?.classe_energetique || ""
      },
      objectives: {
        objectif_principal: formData.objectives?.objectif_principal || "",
        travaux_envisages: formData.objectives?.travaux_envisages || [],
        autres_travaux: formData.objectives?.autres_travaux || "",
        description_projet: formData.objectives?.description_projet || "",
        budget: formData.objectives?.budget || "",
        delai: formData.objectives?.delai || "",
        pieces_concernees: formData.objectives?.pieces_concernees || []
      },
      selected_pack: formData.selected_pack || "",
      selected_service: formData.selected_service || "",
      rgpd_consent: formData.rgpd_consent || false
    };

    // URL depuis env
    const backendUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;

    try {
      const response = await axios.post(
        `${backendUrl}/api/simulations`,
        payload,
        { timeout: 60000, headers: { 'Content-Type': 'application/json' } }
      );

      if (response.data?.success || response.status === 200) {
        localStorage.removeItem(STORAGE_KEY);
        toast.success("Votre demande a été envoyée avec succès !");
        navigate("/merci");
        return;
      }
    } catch (error) {
      const status = error?.response?.status;
      console.error("Submission error - status:", status, error?.message);

      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        localStorage.removeItem(STORAGE_KEY);
        toast.success("Votre demande a bien été enregistrée !");
        navigate("/merci");
        return;
      }

      if (!error.response) {
        toast.error("Erreur de connexion. Vérifiez votre connexion internet.");
      } else if (status === 422) {
        toast.error("Erreur de validation. Veuillez vérifier vos informations.");
      } else if (status >= 500) {
        toast.error("Erreur serveur. Veuillez réessayer dans quelques instants.");
      } else {
        toast.error("Une erreur est survenue. Veuillez réessayer.");
      }
    } finally {
      setLoading(false);
    }
  };

  const progressPercent = (currentStep / 6) * 100;

  return (
    <div className={embedded ? "" : "min-h-screen bg-gradient-to-br from-secondary via-white to-secondary/50 py-12 px-6"}>
      <div className={embedded ? "" : "max-w-4xl mx-auto"}>
        {/* Header */}
        {!embedded && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <h1 className="font-outfit font-bold text-3xl md:text-4xl text-text-primary mb-4">
            Simulation Énergétique
          </h1>
          <p className="font-manrope text-text-secondary max-w-2xl mx-auto">
            Complétez ce formulaire en 6 étapes pour recevoir votre diagnostic personnalisé 
            et découvrir les aides auxquelles vous avez droit.
          </p>
        </motion.div>
        )}

        {/* Progress Bar */}
        <div className="mb-8">
          <div className="flex justify-between items-center mb-3">
            <span className="text-sm font-manrope text-text-secondary">
              Étape {currentStep} sur 6
            </span>
            <span className="text-sm font-manrope text-primary font-semibold">
              {Math.round(progressPercent)}%
            </span>
          </div>
          <Progress value={progressPercent} className="h-3 bg-gray-200" />
        </div>

        {/* Step Indicators */}
        <div className="hidden md:flex justify-between mb-12">
          {STEPS.map((step) => (
            <div
              key={step.id}
              className={`flex flex-col items-center gap-2 ${
                step.id === currentStep ? "text-primary" : 
                step.id < currentStep ? "text-primary/60" : "text-gray-300"
              }`}
            >
              <div className={`w-12 h-12 rounded-full flex items-center justify-center transition-colors ${
                step.id === currentStep ? "bg-primary text-white" :
                step.id < currentStep ? "bg-primary/20 text-primary" : "bg-gray-100 text-gray-400"
              }`}>
                <step.icon size={20} />
              </div>
              <span className="text-xs font-manrope text-center max-w-[80px]">
                {step.title}
              </span>
            </div>
          ))}
        </div>

        {/* Form Card */}
        <motion.div
          className="glass-card bg-white/95 shadow-2xl"
          layout
        >
          <AnimatePresence mode="wait">
            <motion.div
              key={currentStep}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
            >
              {/* Step 1: Personal Info */}
              {currentStep === 1 && (
                <div className="space-y-6">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                      <User className="text-primary" size={24} />
                    </div>
                    <div>
                      <h2 className="font-outfit font-bold text-xl text-text-primary">
                        Informations personnelles
                      </h2>
                      <p className="text-sm text-text-muted font-manrope">
                        Vos coordonnées pour vous recontacter
                      </p>
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="prenom">Prénom *</Label>
                      <Input
                        id="prenom"
                        value={formData.personal_info.prenom}
                        onChange={(e) => updateField("personal_info", "prenom", e.target.value)}
                        placeholder="Jean"
                        className={`input-field ${errors["personal_info.prenom"] ? "border-red-500" : ""}`}
                        data-testid="input-prenom"
                      />
                      {errors["personal_info.prenom"] && (
                        <span className="text-xs text-red-500">{errors["personal_info.prenom"]}</span>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="nom">Nom *</Label>
                      <Input
                        id="nom"
                        value={formData.personal_info.nom}
                        onChange={(e) => updateField("personal_info", "nom", e.target.value)}
                        placeholder="Dupont"
                        className={`input-field ${errors["personal_info.nom"] ? "border-red-500" : ""}`}
                        data-testid="input-nom"
                      />
                      {errors["personal_info.nom"] && (
                        <span className="text-xs text-red-500">{errors["personal_info.nom"]}</span>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email">Email *</Label>
                      <Input
                        id="email"
                        type="email"
                        value={formData.personal_info.email}
                        onChange={(e) => updateField("personal_info", "email", e.target.value)}
                        placeholder="jean.dupont@email.com"
                        className={`input-field ${errors["personal_info.email"] ? "border-red-500" : ""}`}
                        data-testid="input-email"
                      />
                      {errors["personal_info.email"] && (
                        <span className="text-xs text-red-500">{errors["personal_info.email"]}</span>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="telephone">Téléphone *</Label>
                      <Input
                        id="telephone"
                        type="tel"
                        value={formData.personal_info.telephone}
                        onChange={(e) => updateField("personal_info", "telephone", e.target.value)}
                        placeholder="06 12 34 56 78"
                        className={`input-field ${errors["personal_info.telephone"] ? "border-red-500" : ""}`}
                        data-testid="input-telephone"
                      />
                      {errors["personal_info.telephone"] && (
                        <span className="text-xs text-red-500">{errors["personal_info.telephone"]}</span>
                      )}
                    </div>
                    <div className="space-y-2 md:col-span-2">
                      <Label htmlFor="adresse">Adresse du bien *</Label>
                      <Input
                        id="adresse"
                        value={formData.personal_info.adresse}
                        onChange={(e) => updateField("personal_info", "adresse", e.target.value)}
                        placeholder="123 Rue de l'Exemple, 75001 Paris"
                        className={`input-field ${errors["personal_info.adresse"] ? "border-red-500" : ""}`}
                        data-testid="input-adresse"
                      />
                      {errors["personal_info.adresse"] && (
                        <span className="text-xs text-red-500">{errors["personal_info.adresse"]}</span>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Step 2: Property Info */}
              {currentStep === 2 && (
                <div className="space-y-6">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                      <Home className="text-primary" size={24} />
                    </div>
                    <div>
                      <h2 className="font-outfit font-bold text-xl text-text-primary">
                        Caractéristiques du logement
                      </h2>
                      <p className="text-sm text-text-muted font-manrope">
                        Décrivez votre bien immobilier
                      </p>
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label>Type de bien *</Label>
                      <Select
                        value={formData.property_info.type_bien}
                        onValueChange={(v) => updateField("property_info", "type_bien", v)}
                      >
                        <SelectTrigger className={`input-field ${errors["property_info.type_bien"] ? "border-red-500" : ""}`} data-testid="select-type-bien">
                          <SelectValue placeholder="Sélectionnez" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="maison">Maison individuelle</SelectItem>
                          <SelectItem value="appartement">Appartement</SelectItem>
                          <SelectItem value="immeuble">Immeuble</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Année de construction *</Label>
                      <Select
                        value={formData.property_info.annee_construction}
                        onValueChange={(v) => updateField("property_info", "annee_construction", v)}
                      >
                        <SelectTrigger className={`input-field ${errors["property_info.annee_construction"] ? "border-red-500" : ""}`} data-testid="select-annee">
                          <SelectValue placeholder="Sélectionnez" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="avant_1945">Avant 1945</SelectItem>
                          <SelectItem value="1945_1970">1945 - 1970</SelectItem>
                          <SelectItem value="1970_1990">1970 - 1990</SelectItem>
                          <SelectItem value="1990_2005">1990 - 2005</SelectItem>
                          <SelectItem value="2005_2012">2005 - 2012</SelectItem>
                          <SelectItem value="apres_2012">Après 2012</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="surface">Surface habitable (m²) *</Label>
                      <Input
                        id="surface"
                        type="number"
                        value={formData.property_info.surface}
                        onChange={(e) => updateField("property_info", "surface", e.target.value)}
                        placeholder="100"
                        className={`input-field ${errors["property_info.surface"] ? "border-red-500" : ""}`}
                        data-testid="input-surface"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="pieces">Nombre de pièces</Label>
                      <Input
                        id="pieces"
                        type="number"
                        value={formData.property_info.nombre_pieces}
                        onChange={(e) => updateField("property_info", "nombre_pieces", e.target.value)}
                        placeholder="5"
                        className="input-field"
                        data-testid="input-pieces"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="etages">Nombre d'étages</Label>
                      <Input
                        id="etages"
                        type="number"
                        value={formData.property_info.nombre_etages}
                        onChange={(e) => updateField("property_info", "nombre_etages", e.target.value)}
                        placeholder="2"
                        className="input-field"
                        data-testid="input-etages"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Type de vitrage</Label>
                      <Select
                        value={formData.property_info.type_vitrage}
                        onValueChange={(v) => updateField("property_info", "type_vitrage", v)}
                      >
                        <SelectTrigger className="input-field" data-testid="select-vitrage">
                          <SelectValue placeholder="Sélectionnez" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="simple">Simple vitrage</SelectItem>
                          <SelectItem value="double">Double vitrage</SelectItem>
                          <SelectItem value="triple">Triple vitrage</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
              )}

              {/* Step 3: Heating Info */}
              {currentStep === 3 && (
                <div className="space-y-6">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                      <Flame className="text-primary" size={24} />
                    </div>
                    <div>
                      <h2 className="font-outfit font-bold text-xl text-text-primary">
                        Système de chauffage actuel
                      </h2>
                      <p className="text-sm text-text-muted font-manrope">
                        Vos équipements de chauffage et ventilation
                      </p>
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label>Type de chauffage *</Label>
                      <Select
                        value={formData.heating_info.type_chauffage}
                        onValueChange={(v) => updateField("heating_info", "type_chauffage", v)}
                      >
                        <SelectTrigger className={`input-field ${errors["heating_info.type_chauffage"] ? "border-red-500" : ""}`} data-testid="select-chauffage">
                          <SelectValue placeholder="Sélectionnez" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="electrique">Électrique</SelectItem>
                          <SelectItem value="gaz">Gaz</SelectItem>
                          <SelectItem value="fioul">Fioul</SelectItem>
                          <SelectItem value="bois">Bois</SelectItem>
                          <SelectItem value="pompe_chaleur">Pompe à chaleur</SelectItem>
                          <SelectItem value="autre">Autre</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Âge du système</Label>
                      <Select
                        value={formData.heating_info.age_chauffage}
                        onValueChange={(v) => updateField("heating_info", "age_chauffage", v)}
                      >
                        <SelectTrigger className="input-field" data-testid="select-age-chauffage">
                          <SelectValue placeholder="Sélectionnez" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="moins_5">Moins de 5 ans</SelectItem>
                          <SelectItem value="5_10">5 à 10 ans</SelectItem>
                          <SelectItem value="10_15">10 à 15 ans</SelectItem>
                          <SelectItem value="plus_15">Plus de 15 ans</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Production d'eau chaude</Label>
                      <Select
                        value={formData.heating_info.type_eau_chaude}
                        onValueChange={(v) => updateField("heating_info", "type_eau_chaude", v)}
                      >
                        <SelectTrigger className="input-field" data-testid="select-eau-chaude">
                          <SelectValue placeholder="Sélectionnez" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="electrique">Ballon électrique</SelectItem>
                          <SelectItem value="gaz">Chaudière gaz</SelectItem>
                          <SelectItem value="thermodynamique">Thermodynamique</SelectItem>
                          <SelectItem value="solaire">Solaire</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Ventilation</Label>
                      <Select
                        value={formData.heating_info.ventilation}
                        onValueChange={(v) => updateField("heating_info", "ventilation", v)}
                      >
                        <SelectTrigger className="input-field" data-testid="select-ventilation">
                          <SelectValue placeholder="Sélectionnez" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="aucune">Aucune</SelectItem>
                          <SelectItem value="vmc_simple">VMC simple flux</SelectItem>
                          <SelectItem value="vmc_double">VMC double flux</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
              )}

              {/* Step 4: Energy Consumption */}
              {currentStep === 4 && (
                <div className="space-y-6">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                      <Zap className="text-primary" size={24} />
                    </div>
                    <div>
                      <h2 className="font-outfit font-bold text-xl text-text-primary">
                        Consommation énergétique
                      </h2>
                      <p className="text-sm text-text-muted font-manrope">
                        Vos dépenses énergétiques actuelles
                      </p>
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="facture">Facture annuelle estimée (€) *</Label>
                      <Input
                        id="facture"
                        type="number"
                        value={formData.energy_consumption.facture_annuelle}
                        onChange={(e) => updateField("energy_consumption", "facture_annuelle", e.target.value)}
                        placeholder="2000"
                        className={`input-field ${errors["energy_consumption.facture_annuelle"] ? "border-red-500" : ""}`}
                        data-testid="input-facture"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="fournisseur">Fournisseur d'énergie</Label>
                      <Input
                        id="fournisseur"
                        value={formData.energy_consumption.fournisseur}
                        onChange={(e) => updateField("energy_consumption", "fournisseur", e.target.value)}
                        placeholder="EDF, Engie..."
                        className="input-field"
                        data-testid="input-fournisseur"
                      />
                    </div>
                    <div className="space-y-2 md:col-span-2">
                      <Label>Classe énergétique actuelle (si connue)</Label>
                      <div className="flex flex-wrap gap-3 mt-2">
                        {["A", "B", "C", "D", "E", "F", "G", "Inconnue"].map((classe) => {
                          const colors = {
                            A: "bg-green-500",
                            B: "bg-green-400",
                            C: "bg-lime-400",
                            D: "bg-yellow-400",
                            E: "bg-orange-400",
                            F: "bg-orange-500",
                            G: "bg-red-500",
                            Inconnue: "bg-gray-400"
                          };
                          const isSelected = formData.energy_consumption.classe_energetique === classe;
                          return (
                            <button
                              key={classe}
                              type="button"
                              onClick={() => updateField("energy_consumption", "classe_energetique", classe)}
                              className={`w-14 h-14 rounded-xl font-outfit font-bold text-white transition-transform ${colors[classe]} ${
                                isSelected ? "ring-4 ring-primary ring-offset-2 scale-110" : "hover:scale-105"
                              }`}
                              data-testid={`classe-${classe}`}
                            >
                              {classe === "Inconnue" ? "?" : classe}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Step 5: Objectives */}
              {currentStep === 5 && (
                <div className="space-y-6">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                      <Target className="text-primary" size={24} />
                    </div>
                    <div>
                      <h2 className="font-outfit font-bold text-xl text-text-primary">
                        Objectifs et priorités
                      </h2>
                      <p className="text-sm text-text-muted font-manrope">
                        Vos attentes pour ce projet
                      </p>
                    </div>
                  </div>

                  <div className="space-y-6">
                    <div className="space-y-2">
                      <Label>Objectif principal *</Label>
                      <Select
                        value={formData.objectives.objectif_principal}
                        onValueChange={(v) => updateField("objectives", "objectif_principal", v)}
                      >
                        <SelectTrigger className={`input-field ${errors["objectives.objectif_principal"] ? "border-red-500" : ""}`} data-testid="select-objectif">
                          <SelectValue placeholder="Sélectionnez" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="reduire_factures">Réduire mes factures</SelectItem>
                          <SelectItem value="ameliorer_confort">Améliorer mon confort</SelectItem>
                          <SelectItem value="valoriser_bien">Valoriser mon bien</SelectItem>
                          <SelectItem value="environnement">Respecter l'environnement</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-3">
                      <Label>Travaux envisagés * (plusieurs choix possibles)</Label>
                      {errors["objectives.travaux_envisages"] && (
                        <span className="text-xs text-red-500">{errors["objectives.travaux_envisages"]}</span>
                      )}
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                        {[
                          { id: "isolation", label: "Isolation" },
                          { id: "chauffage", label: "Chauffage" },
                          { id: "fenetres", label: "Fenêtres" },
                          { id: "ventilation", label: "Ventilation" },
                          { id: "solaire", label: "Panneaux solaires" },
                          { id: "electricite", label: "Électricité" },
                          { id: "peinture", label: "Peinture" },
                          { id: "autre", label: "Autre" }
                        ].map((travail) => {
                          const travauxList = formData.objectives?.travaux_envisages || [];
                          const isSelected = travauxList.includes(travail.id);
                          return (
                            <button
                              key={travail.id}
                              type="button"
                              onClick={() => toggleTravaux(travail.id)}
                              className={`p-4 rounded-xl border-2 text-left transition-all ${
                                isSelected 
                                  ? "border-primary bg-primary/10 text-primary" 
                                  : "border-gray-200 hover:border-primary/50"
                              }`}
                              data-testid={`travaux-${travail.id}`}
                            >
                              <span className="font-manrope font-medium text-sm">{travail.label}</span>
                            </button>
                          );
                        })}
                      </div>
                      
                      {/* Dimensions des pièces concernées */}
                      {(formData.objectives?.travaux_envisages?.length || 0) > 0 && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: "auto" }}
                          className="mt-6 p-6 bg-gray-50 rounded-2xl border border-gray-200"
                        >
                          <h4 className="font-outfit font-semibold text-text-primary mb-4">
                            📐 Dimensions des pièces concernées
                          </h4>
                          <p className="text-sm text-text-muted mb-4">
                            Ces informations nous permettent d'estimer plus précisément votre projet
                          </p>
                          
                          {/* Sélection de la pièce */}
                          <div className="grid md:grid-cols-4 gap-4 mb-4">
                            <div className="space-y-2">
                              <Label>Pièce</Label>
                              <Select
                                value={(formData.objectives?.pieces_concernees || [])[0]?.nom || ""}
                                onValueChange={(v) => {
                                  const pieces = [...(formData.objectives?.pieces_concernees || [])];
                                  if (pieces.length === 0) {
                                    pieces.push({ nom: v, longueur: "", largeur: "", hauteur: "" });
                                  } else {
                                    pieces[0] = { ...pieces[0], nom: v };
                                  }
                                  setFormData(prev => ({
                                    ...prev,
                                    objectives: { ...prev.objectives, pieces_concernees: pieces }
                                  }));
                                }}
                              >
                                <SelectTrigger className="input-field" data-testid="select-piece">
                                  <SelectValue placeholder="Sélectionnez" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="salon">Salon</SelectItem>
                                  <SelectItem value="chambre">Chambre</SelectItem>
                                  <SelectItem value="cuisine">Cuisine</SelectItem>
                                  <SelectItem value="salle_de_bain">Salle de bain</SelectItem>
                                  <SelectItem value="combles">Combles</SelectItem>
                                  <SelectItem value="garage">Garage</SelectItem>
                                  <SelectItem value="ensemble_maison">Ensemble de la maison</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            <div className="space-y-2">
                              <Label>Longueur (m)</Label>
                              <Input
                                type="number"
                                step="0.1"
                                placeholder="Ex: 5.5"
                                value={(formData.objectives?.pieces_concernees || [])[0]?.longueur || ""}
                                onChange={(e) => {
                                  const pieces = [...(formData.objectives?.pieces_concernees || [])];
                                  if (pieces.length === 0) {
                                    pieces.push({ nom: "", longueur: e.target.value, largeur: "", hauteur: "" });
                                  } else {
                                    pieces[0] = { ...pieces[0], longueur: e.target.value };
                                  }
                                  setFormData(prev => ({
                                    ...prev,
                                    objectives: { ...prev.objectives, pieces_concernees: pieces }
                                  }));
                                }}
                                className="input-field"
                                data-testid="input-longueur"
                              />
                            </div>
                            <div className="space-y-2">
                              <Label>Largeur (m)</Label>
                              <Input
                                type="number"
                                step="0.1"
                                placeholder="Ex: 4.0"
                                value={(formData.objectives?.pieces_concernees || [])[0]?.largeur || ""}
                                onChange={(e) => {
                                  const pieces = [...(formData.objectives?.pieces_concernees || [])];
                                  if (pieces.length === 0) {
                                    pieces.push({ nom: "", longueur: "", largeur: e.target.value, hauteur: "" });
                                  } else {
                                    pieces[0] = { ...pieces[0], largeur: e.target.value };
                                  }
                                  setFormData(prev => ({
                                    ...prev,
                                    objectives: { ...prev.objectives, pieces_concernees: pieces }
                                  }));
                                }}
                                className="input-field"
                                data-testid="input-largeur"
                              />
                            </div>
                            <div className="space-y-2">
                              <Label>Hauteur (m)</Label>
                              <Input
                                type="number"
                                step="0.1"
                                placeholder="Ex: 2.5"
                                value={(formData.objectives?.pieces_concernees || [])[0]?.hauteur || ""}
                                onChange={(e) => {
                                  const pieces = [...(formData.objectives?.pieces_concernees || [])];
                                  if (pieces.length === 0) {
                                    pieces.push({ nom: "", longueur: "", largeur: "", hauteur: e.target.value });
                                  } else {
                                    pieces[0] = { ...pieces[0], hauteur: e.target.value };
                                  }
                                  setFormData(prev => ({
                                    ...prev,
                                    objectives: { ...prev.objectives, pieces_concernees: pieces }
                                  }));
                                }}
                                className="input-field"
                                data-testid="input-hauteur"
                              />
                            </div>
                          </div>
                          
                          {(formData.objectives?.pieces_concernees || [])[0]?.longueur && 
                           (formData.objectives?.pieces_concernees || [])[0]?.largeur && (
                            <div className="mt-2 p-3 bg-primary/10 rounded-xl">
                              <p className="text-sm text-primary font-medium">
                                📊 Surface calculée : {(
                                  parseFloat((formData.objectives?.pieces_concernees || [])[0]?.longueur || 0) *
                                  parseFloat((formData.objectives?.pieces_concernees || [])[0]?.largeur || 0)
                                ).toFixed(1)} m²
                                {(formData.objectives?.pieces_concernees || [])[0]?.hauteur && (
                                  <span className="ml-2">| Volume : {(
                                    parseFloat((formData.objectives?.pieces_concernees || [])[0]?.longueur || 0) *
                                    parseFloat((formData.objectives?.pieces_concernees || [])[0]?.largeur || 0) *
                                    parseFloat((formData.objectives?.pieces_concernees || [])[0]?.hauteur || 0)
                                  ).toFixed(1)} m³</span>
                                )}
                              </p>
                            </div>
                          )}
                        </motion.div>
                      )}
                      
                      {/* Zone "Décrivez votre projet" - Toujours visible */}
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        className="mt-6 p-6 bg-emerald-50 rounded-2xl border border-emerald-200"
                      >
                        <Label htmlFor="description_projet" className="text-lg font-outfit font-semibold text-emerald-900">
                          📝 Décrivez votre projet
                        </Label>
                        <p className="text-sm text-emerald-700 mb-4 mt-1">
                          Expliquez-nous en détail les travaux que vous souhaitez réaliser (optionnel)
                        </p>
                        <textarea
                          id="description_projet"
                          value={formData.objectives?.description_projet || ""}
                          onChange={(e) => {
                            setFormData(prev => ({
                              ...prev,
                              objectives: { 
                                ...(prev.objectives || {}), 
                                description_projet: e.target.value
                              }
                            }));
                          }}
                          placeholder="Exemple : Je souhaite rénover ma salle de bain avec un système de chauffage au sol, remplacer les anciens radiateurs par des modèles plus économes, et améliorer l'isolation des fenêtres du premier étage. Mon objectif principal est de réduire ma facture énergétique..."
                          className="w-full h-32 p-4 bg-white border border-emerald-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 resize-none font-manrope text-sm"
                          data-testid="textarea-description-projet"
                        />
                      </motion.div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label>Budget estimé</Label>
                        <Select
                          value={formData.objectives.budget}
                          onValueChange={(v) => updateField("objectives", "budget", v)}
                        >
                          <SelectTrigger className="input-field" data-testid="select-budget">
                            <SelectValue placeholder="Sélectionnez" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="moins_5000">Moins de 5 000 €</SelectItem>
                            <SelectItem value="5000_15000">5 000 - 15 000 €</SelectItem>
                            <SelectItem value="15000_30000">15 000 - 30 000 €</SelectItem>
                            <SelectItem value="30000_50000">30 000 - 50 000 €</SelectItem>
                            <SelectItem value="plus_50000">Plus de 50 000 €</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Délai souhaité</Label>
                        <Select
                          value={formData.objectives.delai}
                          onValueChange={(v) => updateField("objectives", "delai", v)}
                        >
                          <SelectTrigger className="input-field" data-testid="select-delai">
                            <SelectValue placeholder="Sélectionnez" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="moins_3_mois">Moins de 3 mois</SelectItem>
                            <SelectItem value="3_6_mois">3 à 6 mois</SelectItem>
                            <SelectItem value="6_12_mois">6 à 12 mois</SelectItem>
                            <SelectItem value="plus_12_mois">Plus de 12 mois</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Step 6: Confirmation */}
              {currentStep === 6 && (
                <div className="space-y-6">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                      <CheckCircle className="text-primary" size={24} />
                    </div>
                    <div>
                      <h2 className="font-outfit font-bold text-xl text-text-primary">
                        Confirmation et envoi
                      </h2>
                      <p className="text-sm text-text-muted font-manrope">
                        Vérifiez vos informations
                      </p>
                    </div>
                  </div>

                  {/* Summary */}
                  <div className="space-y-4 bg-secondary/50 rounded-2xl p-6">
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <h4 className="font-outfit font-semibold text-text-primary mb-2">Contact</h4>
                        <p className="text-sm text-text-secondary font-manrope">
                          {formData.personal_info.prenom} {formData.personal_info.nom}<br />
                          {formData.personal_info.email}<br />
                          {formData.personal_info.telephone}
                        </p>
                      </div>
                      <div>
                        <h4 className="font-outfit font-semibold text-text-primary mb-2">Logement</h4>
                        <p className="text-sm text-text-secondary font-manrope">
                          {formData.property_info.type_bien || "-"}<br />
                          {formData.property_info.surface} m²<br />
                          {formData.property_info.adresse || formData.personal_info.adresse}
                        </p>
                      </div>
                      <div>
                        <h4 className="font-outfit font-semibold text-text-primary mb-2">Chauffage</h4>
                        <p className="text-sm text-text-secondary font-manrope">
                          {formData.heating_info.type_chauffage || "-"}<br />
                          Âge: {formData.heating_info.age_chauffage || "-"}
                        </p>
                      </div>
                      <div>
                        <h4 className="font-outfit font-semibold text-text-primary mb-2">Objectifs</h4>
                        <p className="text-sm text-text-secondary font-manrope">
                          {formData.objectives.objectif_principal || "-"}<br />
                          Budget: {formData.objectives.budget || "-"}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* RGPD Consent */}
                  <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-xl">
                    <Checkbox
                      id="rgpd"
                      checked={formData.rgpd_consent}
                      onCheckedChange={(checked) => setFormData(prev => ({ ...prev, rgpd_consent: checked }))}
                      className="mt-1"
                      data-testid="checkbox-rgpd"
                    />
                    <Label htmlFor="rgpd" className="text-sm text-text-secondary font-manrope cursor-pointer">
                      J'accepte que mes données personnelles soient utilisées dans le cadre de ma demande de simulation 
                      énergétique conformément à la politique de confidentialité et au RGPD. *
                    </Label>
                  </div>
                  {errors["rgpd_consent"] && (
                    <span className="text-xs text-red-500">{errors["rgpd_consent"]}</span>
                  )}
                </div>
              )}
            </motion.div>
          </AnimatePresence>

          {/* Navigation Buttons */}
          <div className="flex justify-between mt-10 pt-6 border-t border-gray-100">
            <Button
              variant="outline"
              onClick={handlePrev}
              disabled={currentStep === 1}
              className="btn-secondary"
              data-testid="btn-prev"
            >
              <ChevronLeft size={18} className="mr-2" />
              Précédent
            </Button>

            {currentStep < 6 ? (
              <Button
                onClick={handleNext}
                className="btn-primary"
                data-testid="btn-next"
              >
                Suivant
                <ChevronRight size={18} className="ml-2" />
              </Button>
            ) : (
              <Button
                onClick={handleSubmit}
                disabled={loading}
                className="btn-primary"
                data-testid="btn-submit"
              >
                {loading ? (
                  <>
                    <Loader2 size={18} className="mr-2 animate-spin" />
                    Envoi en cours...
                  </>
                ) : (
                  <>
                    Recevoir mon analyse
                    <CheckCircle size={18} className="ml-2" />
                  </>
                )}
              </Button>
            )}
          </div>
        </motion.div>
      </div>
    </div>
  );
}
